/**
 * Void Tree — ÁRVORE de build do personagem (POWER / SURVIVAL / AGILITY).
 * Definição (config) separada da lógica e da UI. Pontos são DERIVADOS (level + boss +
 * pontos comprados na Loja), nunca incrementados no save (anti-exploit/anti-desync):
 * available = earned - spent.
 *
 * 10.5L — ÁRVORE DE VERDADE ("genealógica"): cada nó tem UM pai (`parent`) e um `tier`
 * (linha). O filho só abre depois do pai; a UI desenha os fios ligando pai → filhos.
 * A árvore cobre TODOS os atributos: dano por tipo, defesa por tipo, bloqueio, regeneração,
 * alcance, gancho, sprint, crítico, execução.
 */
import { GameConfig } from "../config/GameConfig";

export type Branch = "power" | "survival" | "agility" | "class"; // 10.9CL — "class": nós da CLASSE do personagem

/** Chaves de efeito numérico acumuláveis. */
export type EffectKey =
  | "damagePct"
  | "critChance" // fração somada (0.02 = +2%)
  | "critDamagePct"
  | "attackSpeedPct"
  | "attackRangeFlat" // metros somados ao alcance da arma
  | "maxHpPct"
  | "armorFlat"
  | "moveSpeedPct"
  | "hookRangePct"
  | "hookCdReducPct"
  | "sprintDurationPct"
  | "sprintCdReducPct"
  | "vsElitePct" // dano extra vs Elite/Boss
  | "executePct" // dano extra vs alvo abaixo do limiar
  | "resiliencePct" // redução de dano recebido
  | "dmgSlashPct"
  | "dmgPiercePct"
  | "dmgBluntPct"
  | "defSlashPct"
  | "defPiercePct"
  | "defBluntPct"
  | "blockChance"
  | "regenPct"
  | "lifeStealPct" // 10.9EQ — roubo de vida (% do dano causado)
  // 10.9CL — efeitos da MAGIA DA CLASSE (Thiago: "skills da própria classe na árvore")
  | "spellDamagePct" // dano da magia (nova/golpe/chuva) e dos servos
  | "spellRadiusPct" // raio/alcance da magia
  | "spellCdReducPct" // redução do cooldown da magia
  | "summonCount" // +servos por invocação (flat)
  | "summonLifePct" // +tempo de vida dos servos
  | "summonSpeedPct" // +velocidade de ataque dos servos
  | "summonHpPct" // 10.9CM — +vida (HP) dos servos
  | "spellHealOnCast" // cura % da vida máx. ao lançar a magia
  | "spellCritBonus"; // +chance de crítico da magia

/** Efeitos que NÃO são porcentagem (a UI formata como número puro). */
export const FLAT_EFFECTS: ReadonlySet<EffectKey> = new Set<EffectKey>(["armorFlat", "attackRangeFlat", "summonCount"]);

export interface NodeEffect {
  key: EffectKey;
  perRank: number;
}

export interface VoidNode {
  id: string;
  branch: Branch;
  nameKey: string;
  descKey?: string; // só p/ nós especiais (senão a descrição é gerada dos efeitos)
  tier: number; // linha na árvore (0 = raiz)
  parent: string | null; // pai na árvore (null = raiz da branch)
  maxRank: number;
  cost: number; // custo por rank (Void Points)
  alsoRequires?: string[]; // pré-requisito EXTRA além do pai (nós finais)
  effects: NodeEffect[];
  flag?: string; // nó especial/final (efeito comportamental)
  icon: string;
  /** 10.9CL — nó exclusivo de uma classe (só aparece/compra com ela). */
  classId?: string;
}

export const VOID_NODES: readonly VoidNode[] = [
  // ============================ POWER ============================
  { id: "pow_core", branch: "power", nameKey: "node.pow_core", tier: 0, parent: null, maxRank: 3, cost: 1, icon: "⚔️", effects: [{ key: "damagePct", perRank: 0.04 }] },
  // tipos de dano (cada arma tem o seu)
  { id: "pow_slash", branch: "power", nameKey: "node.pow_slash", tier: 1, parent: "pow_core", maxRank: 3, cost: 1, icon: "🗡️", effects: [{ key: "dmgSlashPct", perRank: 0.05 }] },
  { id: "pow_pierce", branch: "power", nameKey: "node.pow_pierce", tier: 1, parent: "pow_core", maxRank: 3, cost: 1, icon: "🏹", effects: [{ key: "dmgPiercePct", perRank: 0.05 }] },
  { id: "pow_blunt", branch: "power", nameKey: "node.pow_blunt", tier: 1, parent: "pow_core", maxRank: 3, cost: 1, icon: "🔨", effects: [{ key: "dmgBluntPct", perRank: 0.05 }] },
  { id: "pow_atkspd", branch: "power", nameKey: "node.pow_atkspd", tier: 2, parent: "pow_slash", maxRank: 3, cost: 1, icon: "⚡", effects: [{ key: "attackSpeedPct", perRank: 0.04 }] },
  { id: "pow_crit", branch: "power", nameKey: "node.pow_crit", tier: 2, parent: "pow_pierce", maxRank: 3, cost: 1, icon: "🎯", effects: [{ key: "critChance", perRank: 0.02 }] },
  { id: "pow_heavy", branch: "power", nameKey: "node.pow_heavy", tier: 2, parent: "pow_blunt", maxRank: 3, cost: 1, icon: "💥", effects: [{ key: "damagePct", perRank: 0.05 }] },
  { id: "pow_hunter", branch: "power", nameKey: "node.pow_hunter", descKey: "node.pow_hunter.d", tier: 3, parent: "pow_atkspd", maxRank: 3, cost: 1, icon: "👁️", effects: [{ key: "vsElitePct", perRank: 0.05 }] },
  { id: "pow_ferocity", branch: "power", nameKey: "node.pow_ferocity", tier: 3, parent: "pow_crit", maxRank: 3, cost: 1, icon: "🔥", effects: [{ key: "critDamagePct", perRank: 0.12 }] },
  { id: "pow_exec", branch: "power", nameKey: "node.pow_exec", descKey: "node.pow_exec.d", tier: 3, parent: "pow_heavy", maxRank: 3, cost: 1, icon: "☠️", effects: [{ key: "executePct", perRank: 0.07 }] },
  { id: "pow_dominance", branch: "power", nameKey: "node.pow_dominance", descKey: "node.pow_dominance.d", tier: 4, parent: "pow_ferocity", alsoRequires: ["pow_hunter", "pow_exec"], maxRank: 1, cost: 3, icon: "👑", effects: [], flag: "voidDominance" },

  // ========================== SURVIVAL ==========================
  { id: "sur_hp1", branch: "survival", nameKey: "node.sur_hp1", tier: 0, parent: null, maxRank: 3, cost: 1, icon: "❤️", effects: [{ key: "maxHpPct", perRank: 0.05 }] },
  { id: "sur_armor", branch: "survival", nameKey: "node.sur_armor", tier: 1, parent: "sur_hp1", maxRank: 3, cost: 1, icon: "🛡️", effects: [{ key: "armorFlat", perRank: 4 }] },
  { id: "sur_regen", branch: "survival", nameKey: "node.sur_regen", descKey: "node.sur_regen.d", tier: 1, parent: "sur_hp1", maxRank: 3, cost: 1, icon: "💠", effects: [{ key: "regenPct", perRank: 0.005 }] },
  { id: "sur_defslash", branch: "survival", nameKey: "node.sur_defslash", tier: 2, parent: "sur_armor", maxRank: 3, cost: 1, icon: "🗡️", effects: [{ key: "defSlashPct", perRank: 0.05 }] },
  { id: "sur_defpierce", branch: "survival", nameKey: "node.sur_defpierce", tier: 2, parent: "sur_armor", maxRank: 3, cost: 1, icon: "🏹", effects: [{ key: "defPiercePct", perRank: 0.05 }] },
  { id: "sur_defblunt", branch: "survival", nameKey: "node.sur_defblunt", tier: 2, parent: "sur_armor", maxRank: 3, cost: 1, icon: "🔨", effects: [{ key: "defBluntPct", perRank: 0.05 }] },
  { id: "sur_hp2", branch: "survival", nameKey: "node.sur_hp2", tier: 2, parent: "sur_regen", maxRank: 3, cost: 1, icon: "💗", effects: [{ key: "maxHpPct", perRank: 0.06 }] },
  { id: "sur_block", branch: "survival", nameKey: "node.sur_block", descKey: "node.sur_block.d", tier: 3, parent: "sur_defslash", alsoRequires: ["sur_defblunt"], maxRank: 3, cost: 1, icon: "🛑", effects: [{ key: "blockChance", perRank: 0.05 }] },
  { id: "sur_resil", branch: "survival", nameKey: "node.sur_resil", descKey: "node.sur_resil.d", tier: 3, parent: "sur_hp2", maxRank: 3, cost: 1, icon: "🌀", effects: [{ key: "resiliencePct", perRank: 0.03 }] },
  // 10.9EQ — ROUBO DE VIDA: curar batendo. Sede de Sangue cedo no ramo; Pacto Vampirico fundo.
  { id: "sur_leech", branch: "survival", nameKey: "node.sur_leech", descKey: "node.sur_leech.d", tier: 3, parent: "sur_hp2", maxRank: 5, cost: 1, icon: "🩸", effects: [{ key: "lifeStealPct", perRank: 0.01 }] },
  { id: "sur_secondwind", branch: "survival", nameKey: "node.sur_secondwind", descKey: "node.sur_secondwind.d", tier: 4, parent: "sur_resil", maxRank: 1, cost: 2, icon: "💨", effects: [], flag: "secondWind" },
  { id: "sur_unbreakable", branch: "survival", nameKey: "node.sur_unbreakable", descKey: "node.sur_unbreakable.d", tier: 5, parent: "sur_secondwind", alsoRequires: ["sur_block"], maxRank: 1, cost: 3, icon: "🗿", effects: [], flag: "unbreakable" },

  // ========================== AGILITY ===========================
  { id: "agi_move", branch: "agility", nameKey: "node.agi_move", tier: 0, parent: null, maxRank: 3, cost: 1, icon: "👟", effects: [{ key: "moveSpeedPct", perRank: 0.04 }] },
  { id: "agi_atkspd", branch: "agility", nameKey: "node.agi_atkspd", tier: 1, parent: "agi_move", maxRank: 3, cost: 1, icon: "🌪️", effects: [{ key: "attackSpeedPct", perRank: 0.04 }] },
  { id: "agi_sprintdur", branch: "agility", nameKey: "node.agi_sprintdur", tier: 1, parent: "agi_move", maxRank: 3, cost: 1, icon: "🏃", effects: [{ key: "sprintDurationPct", perRank: 0.12 }] },
  { id: "agi_reach", branch: "agility", nameKey: "node.agi_reach", descKey: "node.agi_reach.d", tier: 2, parent: "agi_atkspd", maxRank: 3, cost: 1, icon: "📏", effects: [{ key: "attackRangeFlat", perRank: 0.25 }] },
  { id: "agi_hookrange", branch: "agility", nameKey: "node.agi_hookrange", tier: 2, parent: "agi_atkspd", maxRank: 3, cost: 1, icon: "🪝", effects: [{ key: "hookRangePct", perRank: 0.06 }] },
  { id: "agi_sprintcd", branch: "agility", nameKey: "node.agi_sprintcd", tier: 2, parent: "agi_sprintdur", maxRank: 3, cost: 1, icon: "♻️", effects: [{ key: "sprintCdReducPct", perRank: 0.08 }] },
  { id: "agi_hookcd", branch: "agility", nameKey: "node.agi_hookcd", tier: 3, parent: "agi_hookrange", maxRank: 3, cost: 1, icon: "⏱️", effects: [{ key: "hookCdReducPct", perRank: 0.08 }] },
  { id: "agi_predator", branch: "agility", nameKey: "node.agi_predator", descKey: "node.agi_predator.d", tier: 3, parent: "agi_sprintcd", maxRank: 1, cost: 2, icon: "🐾", effects: [], flag: "predator" },
  { id: "agi_voidstep", branch: "agility", nameKey: "node.agi_voidstep", descKey: "node.agi_voidstep.d", tier: 4, parent: "agi_predator", alsoRequires: ["agi_hookcd"], maxRank: 1, cost: 3, icon: "✨", effects: [], flag: "voidStep" },

  // ============================ 10.7E — TIERS AVANÇADOS (endgame) ============================
  // POWER — 5 novos: 30 pontos extras pra maxar
  { id: "pow_rage", branch: "power", nameKey: "node.pow_rage", tier: 5, parent: "pow_dominance", maxRank: 5, cost: 2, icon: "😤", effects: [{ key: "damagePct", perRank: 0.06 }] },
  { id: "pow_precision", branch: "power", nameKey: "node.pow_precision", tier: 5, parent: "pow_dominance", maxRank: 5, cost: 2, icon: "🔱", effects: [{ key: "critChance", perRank: 0.03 }, { key: "critDamagePct", perRank: 0.15 }] },
  { id: "pow_savagery", branch: "power", nameKey: "node.pow_savagery", tier: 6, parent: "pow_rage", maxRank: 3, cost: 3, icon: "🦁", effects: [{ key: "vsElitePct", perRank: 0.1 }, { key: "executePct", perRank: 0.1 }] },
  { id: "pow_bloodedge", branch: "power", nameKey: "node.pow_bloodedge", tier: 6, parent: "pow_precision", maxRank: 5, cost: 2, icon: "🩸", effects: [{ key: "damagePct", perRank: 0.08 }] },
  { id: "pow_apex", branch: "power", nameKey: "node.pow_apex", descKey: "node.pow_apex.d", tier: 7, parent: "pow_savagery", alsoRequires: ["pow_bloodedge"], maxRank: 1, cost: 5, icon: "🌟", effects: [{ key: "damagePct", perRank: 0.5 }, { key: "attackSpeedPct", perRank: 0.15 }] },

  // SURVIVAL — 5 novos
  { id: "sur_fortitude", branch: "survival", nameKey: "node.sur_fortitude", tier: 6, parent: "sur_unbreakable", maxRank: 5, cost: 2, icon: "❤️", effects: [{ key: "maxHpPct", perRank: 0.10 }] },
  { id: "sur_ironskin", branch: "survival", nameKey: "node.sur_ironskin", tier: 6, parent: "sur_unbreakable", maxRank: 5, cost: 2, icon: "⚙️", effects: [{ key: "armorFlat", perRank: 10 }] },
  { id: "sur_veteran", branch: "survival", nameKey: "node.sur_veteran", tier: 7, parent: "sur_fortitude", maxRank: 3, cost: 3, icon: "🎖️", effects: [{ key: "resiliencePct", perRank: 0.05 }] },
  { id: "sur_vampire", branch: "survival", nameKey: "node.sur_vampire", descKey: "node.sur_vampire.d", tier: 7, parent: "sur_ironskin", maxRank: 3, cost: 3, icon: "🦇", effects: [{ key: "lifeStealPct", perRank: 0.015 }] }, // 10.9EQ
  { id: "sur_regenmaster", branch: "survival", nameKey: "node.sur_regenmaster", tier: 7, parent: "sur_ironskin", maxRank: 3, cost: 3, icon: "🍃", effects: [{ key: "regenPct", perRank: 0.015 }] },
  { id: "sur_bastion", branch: "survival", nameKey: "node.sur_bastion", descKey: "node.sur_bastion.d", tier: 8, parent: "sur_veteran", alsoRequires: ["sur_regenmaster"], maxRank: 1, cost: 5, icon: "🏰", effects: [{ key: "maxHpPct", perRank: 0.5 }, { key: "resiliencePct", perRank: 0.15 }] },

  // AGILITY — 5 novos
  { id: "agi_swiftness", branch: "agility", nameKey: "node.agi_swiftness", tier: 5, parent: "agi_voidstep", maxRank: 5, cost: 2, icon: "💨", effects: [{ key: "moveSpeedPct", perRank: 0.06 }] },
  { id: "agi_flurry", branch: "agility", nameKey: "node.agi_flurry", tier: 5, parent: "agi_voidstep", maxRank: 5, cost: 2, icon: "⚡", effects: [{ key: "attackSpeedPct", perRank: 0.06 }] },
  { id: "agi_dodge", branch: "agility", nameKey: "node.agi_dodge", tier: 6, parent: "agi_swiftness", maxRank: 3, cost: 3, icon: "🌀", effects: [{ key: "blockChance", perRank: 0.05 }] },
  { id: "agi_relentless", branch: "agility", nameKey: "node.agi_relentless", tier: 6, parent: "agi_flurry", maxRank: 3, cost: 3, icon: "🔁", effects: [{ key: "attackSpeedPct", perRank: 0.08 }] },
  { id: "agi_wraith", branch: "agility", nameKey: "node.agi_wraith", descKey: "node.agi_wraith.d", tier: 7, parent: "agi_dodge", alsoRequires: ["agi_relentless"], maxRank: 1, cost: 5, icon: "👻", effects: [{ key: "moveSpeedPct", perRank: 0.3 }, { key: "blockChance", perRank: 0.15 }] },

  // ==================== 10.7N — CONSTELAÇÃO INFINITA (variedade, não "200 num nó") ====================
  // Após o capstone de cada branch abrem VÁRIOS nós infinitos temáticos (maxRank 50 cada).
  // Dá build variada ("infinitas possibilidades") + retenção — 9 nós × 50 = 450 pts de sink.
  // POWER
  { id: "pow_inf_dmg", branch: "power", nameKey: "node.pow_inf_dmg", tier: 8, parent: "pow_apex", maxRank: 50, cost: 1, icon: "⚔️", effects: [{ key: "damagePct", perRank: 0.02 }] },
  { id: "pow_inf_crit", branch: "power", nameKey: "node.pow_inf_crit", tier: 8, parent: "pow_apex", maxRank: 50, cost: 1, icon: "🎯", effects: [{ key: "critChance", perRank: 0.004 }, { key: "critDamagePct", perRank: 0.03 }] },
  { id: "pow_inf_atkspd", branch: "power", nameKey: "node.pow_inf_atkspd", tier: 8, parent: "pow_apex", maxRank: 50, cost: 1, icon: "⚡", effects: [{ key: "attackSpeedPct", perRank: 0.012 }] },
  // SURVIVAL
  { id: "sur_inf_hp", branch: "survival", nameKey: "node.sur_inf_hp", tier: 9, parent: "sur_bastion", maxRank: 50, cost: 1, icon: "❤️", effects: [{ key: "maxHpPct", perRank: 0.02 }] },
  { id: "sur_inf_armor", branch: "survival", nameKey: "node.sur_inf_armor", tier: 9, parent: "sur_bastion", maxRank: 50, cost: 1, icon: "🛡️", effects: [{ key: "armorFlat", perRank: 8 }] },
  { id: "sur_inf_regen", branch: "survival", nameKey: "node.sur_inf_regen", tier: 9, parent: "sur_bastion", maxRank: 50, cost: 1, icon: "💠", effects: [{ key: "regenPct", perRank: 0.004 }] },
  // AGILITY
  { id: "agi_inf_speed", branch: "agility", nameKey: "node.agi_inf_speed", tier: 8, parent: "agi_wraith", maxRank: 50, cost: 1, icon: "👟", effects: [{ key: "moveSpeedPct", perRank: 0.012 }] },
  { id: "agi_inf_atkspd", branch: "agility", nameKey: "node.agi_inf_atkspd", tier: 8, parent: "agi_wraith", maxRank: 50, cost: 1, icon: "🌪️", effects: [{ key: "attackSpeedPct", perRank: 0.012 }] },
  { id: "agi_inf_range", branch: "agility", nameKey: "node.agi_inf_range", tier: 8, parent: "agi_wraith", maxRank: 50, cost: 1, icon: "📏", effects: [{ key: "attackRangeFlat", perRank: 0.12 }] },

  // ============================ CLASSE (10.9CL) ============================
  // Uma mini-árvore por classe (só a da classe ativa aparece). Raiz = dano da magia; ramos = raio /
  // cooldown; nó final = "assinatura" da classe. Necromante: servos (dano, vida=tempo, QUANTIDADE).
  // -- Guerreiro: Golpe Sísmico
  { id: "cls_war_dmg", classId: "warrior", branch: "class", nameKey: "node.cls_war_dmg", tier: 0, parent: null, maxRank: 5, cost: 1, icon: "💥", effects: [{ key: "spellDamagePct", perRank: 0.1 }] },
  { id: "cls_war_radius", classId: "warrior", branch: "class", nameKey: "node.cls_war_radius", tier: 1, parent: "cls_war_dmg", maxRank: 3, cost: 1, icon: "🌊", effects: [{ key: "spellRadiusPct", perRank: 0.12 }] },
  { id: "cls_war_cd", classId: "warrior", branch: "class", nameKey: "node.cls_war_cd", tier: 1, parent: "cls_war_dmg", maxRank: 3, cost: 1, icon: "⏱️", effects: [{ key: "spellCdReducPct", perRank: 0.08 }] },
  { id: "cls_war_quake", classId: "warrior", branch: "class", nameKey: "node.cls_war_quake", tier: 2, parent: "cls_war_radius", alsoRequires: ["cls_war_cd"], maxRank: 3, cost: 2, icon: "🏔️", effects: [{ key: "spellDamagePct", perRank: 0.15 }, { key: "spellRadiusPct", perRank: 0.06 }] },
  // -- Arqueiro: Chuva de Flechas
  { id: "cls_arc_dmg", classId: "archer", branch: "class", nameKey: "node.cls_arc_dmg", tier: 0, parent: null, maxRank: 5, cost: 1, icon: "🏹", effects: [{ key: "spellDamagePct", perRank: 0.1 }] },
  { id: "cls_arc_radius", classId: "archer", branch: "class", nameKey: "node.cls_arc_radius", tier: 1, parent: "cls_arc_dmg", maxRank: 3, cost: 1, icon: "📏", effects: [{ key: "spellRadiusPct", perRank: 0.12 }] },
  { id: "cls_arc_cd", classId: "archer", branch: "class", nameKey: "node.cls_arc_cd", tier: 1, parent: "cls_arc_dmg", maxRank: 3, cost: 1, icon: "⏱️", effects: [{ key: "spellCdReducPct", perRank: 0.08 }] },
  { id: "cls_arc_storm", classId: "archer", branch: "class", nameKey: "node.cls_arc_storm", tier: 2, parent: "cls_arc_radius", alsoRequires: ["cls_arc_cd"], maxRank: 3, cost: 2, icon: "🌪️", effects: [{ key: "spellDamagePct", perRank: 0.15 }, { key: "spellCdReducPct", perRank: 0.04 }] },
  // -- Mago: Nova Arcana
  { id: "cls_mag_dmg", classId: "mage", branch: "class", nameKey: "node.cls_mag_dmg", tier: 0, parent: null, maxRank: 5, cost: 1, icon: "🔮", effects: [{ key: "spellDamagePct", perRank: 0.1 }] },
  { id: "cls_mag_radius", classId: "mage", branch: "class", nameKey: "node.cls_mag_radius", tier: 1, parent: "cls_mag_dmg", maxRank: 3, cost: 1, icon: "🌀", effects: [{ key: "spellRadiusPct", perRank: 0.12 }] },
  { id: "cls_mag_cd", classId: "mage", branch: "class", nameKey: "node.cls_mag_cd", tier: 1, parent: "cls_mag_dmg", maxRank: 3, cost: 1, icon: "⏱️", effects: [{ key: "spellCdReducPct", perRank: 0.08 }] },
  { id: "cls_mag_cataclysm", classId: "mage", branch: "class", nameKey: "node.cls_mag_cataclysm", tier: 2, parent: "cls_mag_radius", alsoRequires: ["cls_mag_cd"], maxRank: 3, cost: 2, icon: "☄️", effects: [{ key: "spellDamagePct", perRank: 0.18 }] },
  // -- Necromante: Exército do Vazio (servos)
  { id: "cls_nec_dmg", classId: "necromancer", branch: "class", nameKey: "node.cls_nec_dmg", tier: 0, parent: null, maxRank: 5, cost: 1, icon: "💀", effects: [{ key: "spellDamagePct", perRank: 0.1 }] },
  { id: "cls_nec_life", classId: "necromancer", branch: "class", nameKey: "node.cls_nec_life", descKey: "node.cls_nec_life.d", tier: 1, parent: "cls_nec_dmg", maxRank: 3, cost: 1, icon: "⏳", effects: [{ key: "summonLifePct", perRank: 0.25 }] },
  { id: "cls_nec_cd", classId: "necromancer", branch: "class", nameKey: "node.cls_nec_cd", tier: 1, parent: "cls_nec_dmg", maxRank: 3, cost: 1, icon: "⏱️", effects: [{ key: "spellCdReducPct", perRank: 0.08 }] },
  { id: "cls_nec_speed", classId: "necromancer", branch: "class", nameKey: "node.cls_nec_speed", tier: 2, parent: "cls_nec_life", maxRank: 3, cost: 1, icon: "⚡", effects: [{ key: "summonSpeedPct", perRank: 0.12 }] },
  { id: "cls_nec_count", classId: "necromancer", branch: "class", nameKey: "node.cls_nec_count", descKey: "node.cls_nec_count.d", tier: 2, parent: "cls_nec_cd", alsoRequires: ["cls_nec_life"], maxRank: 2, cost: 2, icon: "🧟", effects: [{ key: "summonCount", perRank: 1 }] },
  { id: "cls_nec_legion", classId: "necromancer", branch: "class", nameKey: "node.cls_nec_legion", tier: 3, parent: "cls_nec_count", alsoRequires: ["cls_nec_speed"], maxRank: 3, cost: 2, icon: "👑", effects: [{ key: "spellDamagePct", perRank: 0.12 }, { key: "summonLifePct", perRank: 0.1 }] },

  // ---- 10.9CM — +2 habilidades por classe (Thiago) ----
  // Guerreiro
  { id: "cls_war_rage", classId: "warrior", branch: "class", nameKey: "node.cls_war_rage", tier: 3, parent: "cls_war_quake", maxRank: 3, cost: 2, icon: "🩸", effects: [{ key: "spellDamagePct", perRank: 0.15 }, { key: "spellCritBonus", perRank: 0.05 }] },
  { id: "cls_war_guard", classId: "warrior", branch: "class", nameKey: "node.cls_war_guard", descKey: "node.cls_war_guard.d", tier: 4, parent: "cls_war_rage", maxRank: 3, cost: 2, icon: "🛡️", effects: [{ key: "spellHealOnCast", perRank: 0.05 }] },
  // Arqueiro
  { id: "cls_arc_pierce", classId: "archer", branch: "class", nameKey: "node.cls_arc_pierce", tier: 3, parent: "cls_arc_storm", maxRank: 3, cost: 2, icon: "🎯", effects: [{ key: "spellDamagePct", perRank: 0.12 }, { key: "spellCritBonus", perRank: 0.06 }] },
  { id: "cls_arc_rain", classId: "archer", branch: "class", nameKey: "node.cls_arc_rain", tier: 4, parent: "cls_arc_pierce", maxRank: 3, cost: 2, icon: "🌧️", effects: [{ key: "spellRadiusPct", perRank: 0.12 }, { key: "spellCdReducPct", perRank: 0.05 }] },
  // Mago
  { id: "cls_mag_surge", classId: "mage", branch: "class", nameKey: "node.cls_mag_surge", tier: 3, parent: "cls_mag_cataclysm", maxRank: 3, cost: 2, icon: "⚡", effects: [{ key: "spellDamagePct", perRank: 0.15 }, { key: "spellCritBonus", perRank: 0.06 }] },
  { id: "cls_mag_font", classId: "mage", branch: "class", nameKey: "node.cls_mag_font", descKey: "node.cls_mag_font.d", tier: 4, parent: "cls_mag_surge", maxRank: 3, cost: 2, icon: "💧", effects: [{ key: "spellHealOnCast", perRank: 0.05 }] },
  // Necromante — VIDA (HP) dos servos + colheita
  { id: "cls_nec_hp", classId: "necromancer", branch: "class", nameKey: "node.cls_nec_hp", descKey: "node.cls_nec_hp.d", tier: 2, parent: "cls_nec_dmg", maxRank: 3, cost: 1, icon: "🩸", effects: [{ key: "summonHpPct", perRank: 0.3 }] },
  { id: "cls_nec_reap", classId: "necromancer", branch: "class", nameKey: "node.cls_nec_reap", tier: 4, parent: "cls_nec_legion", maxRank: 3, cost: 2, icon: "🌾", effects: [{ key: "spellDamagePct", perRank: 0.12 }, { key: "summonHpPct", perRank: 0.15 }] },

  // ===========================================================================================
  // 10.9EF — MAESTRIA DE CLASSE. O ramo da classe tinha 20 ranks e enchia em ~2h de jogo: a
  // parte mais identitaria da arvore acabava antes de tudo, e sobravam ~1000h so enchendo os
  // ramos genericos (iguais pra todo mundo). Aqui ele passa a ter a MESMA estrutura dos ramos
  // comuns: um CAPSTONE que fecha o desenho, e tres nos INFINITOS (rank 50) tematicos da classe.
  // 20 -> 171 ranks (necromante 25 -> 176), em linha com Poder 200 / Sobrevivencia 196.
  // Todos os efeitos usados aqui ja existem e ja estao ligados — nenhum sistema novo.
  // ===========================================================================================

  // GUERREIRO — muralha que avanca: dano bruto, armadura e o proprio golpe
  { id: "cls_war_apex", classId: "warrior", branch: "class", nameKey: "node.cls_war_apex", descKey: "node.cls_war_apex.d", tier: 5, parent: "cls_war_guard", maxRank: 1, cost: 3, icon: "⚔️", effects: [{ key: "damagePct", perRank: 0.2 }, { key: "armorFlat", perRank: 20 }, { key: "spellDamagePct", perRank: 0.25 }] },
  { id: "cls_war_inf_might", classId: "warrior", branch: "class", nameKey: "node.cls_war_inf_might", tier: 6, parent: "cls_war_apex", maxRank: 50, cost: 1, icon: "💪", effects: [{ key: "damagePct", perRank: 0.02 }, { key: "dmgBluntPct", perRank: 0.01 }] },
  { id: "cls_war_inf_bulwark", classId: "warrior", branch: "class", nameKey: "node.cls_war_inf_bulwark", tier: 6, parent: "cls_war_apex", maxRank: 50, cost: 1, icon: "🛡️", effects: [{ key: "armorFlat", perRank: 1.5 }, { key: "resiliencePct", perRank: 0.003 }] },
  { id: "cls_war_inf_quake", classId: "warrior", branch: "class", nameKey: "node.cls_war_inf_quake", tier: 6, parent: "cls_war_apex", maxRank: 50, cost: 1, icon: "🏔️", effects: [{ key: "spellDamagePct", perRank: 0.02 }, { key: "spellRadiusPct", perRank: 0.006 }] },

  // ARQUEIRO — distancia, critico e velocidade
  { id: "cls_arc_apex", classId: "archer", branch: "class", nameKey: "node.cls_arc_apex", descKey: "node.cls_arc_apex.d", tier: 5, parent: "cls_arc_rain", maxRank: 1, cost: 3, icon: "🏹", effects: [{ key: "dmgPiercePct", perRank: 0.2 }, { key: "attackRangeFlat", perRank: 1.5 }, { key: "spellDamagePct", perRank: 0.25 }] },
  { id: "cls_arc_inf_aim", classId: "archer", branch: "class", nameKey: "node.cls_arc_inf_aim", tier: 6, parent: "cls_arc_apex", maxRank: 50, cost: 1, icon: "🎯", effects: [{ key: "dmgPiercePct", perRank: 0.02 }, { key: "critChance", perRank: 0.003 }] },
  { id: "cls_arc_inf_swift", classId: "archer", branch: "class", nameKey: "node.cls_arc_inf_swift", tier: 6, parent: "cls_arc_apex", maxRank: 50, cost: 1, icon: "💨", effects: [{ key: "attackSpeedPct", perRank: 0.012 }, { key: "moveSpeedPct", perRank: 0.004 }] },
  { id: "cls_arc_inf_volley", classId: "archer", branch: "class", nameKey: "node.cls_arc_inf_volley", tier: 6, parent: "cls_arc_apex", maxRank: 50, cost: 1, icon: "🌧️", effects: [{ key: "spellDamagePct", perRank: 0.02 }, { key: "spellRadiusPct", perRank: 0.008 }] },

  // MAGO — magia pura: dano, fluxo e critico de magia
  { id: "cls_mag_apex", classId: "mage", branch: "class", nameKey: "node.cls_mag_apex", descKey: "node.cls_mag_apex.d", tier: 5, parent: "cls_mag_font", maxRank: 1, cost: 3, icon: "🔮", effects: [{ key: "spellDamagePct", perRank: 0.4 }, { key: "spellRadiusPct", perRank: 0.15 }] },
  { id: "cls_mag_inf_arcane", classId: "mage", branch: "class", nameKey: "node.cls_mag_inf_arcane", tier: 6, parent: "cls_mag_apex", maxRank: 50, cost: 1, icon: "✨", effects: [{ key: "spellDamagePct", perRank: 0.03 }] },
  { id: "cls_mag_inf_flow", classId: "mage", branch: "class", nameKey: "node.cls_mag_inf_flow", tier: 6, parent: "cls_mag_apex", maxRank: 50, cost: 1, icon: "🌀", effects: [{ key: "spellCdReducPct", perRank: 0.004 }, { key: "spellRadiusPct", perRank: 0.008 }] },
  { id: "cls_mag_inf_focus", classId: "mage", branch: "class", nameKey: "node.cls_mag_inf_focus", tier: 6, parent: "cls_mag_apex", maxRank: 50, cost: 1, icon: "🎆", effects: [{ key: "spellCritBonus", perRank: 0.004 }, { key: "critDamagePct", perRank: 0.03 }] },

  // NECROMANTE — a legiao: mais servos vivos, mais tempo, mais forte
  { id: "cls_nec_apex", classId: "necromancer", branch: "class", nameKey: "node.cls_nec_apex", descKey: "node.cls_nec_apex.d", tier: 5, parent: "cls_nec_reap", maxRank: 1, cost: 3, icon: "👑", effects: [{ key: "summonCount", perRank: 1 }, { key: "summonHpPct", perRank: 0.5 }, { key: "spellDamagePct", perRank: 0.25 }] },
  { id: "cls_nec_inf_horde", classId: "necromancer", branch: "class", nameKey: "node.cls_nec_inf_horde", tier: 6, parent: "cls_nec_apex", maxRank: 50, cost: 1, icon: "🧟", effects: [{ key: "summonHpPct", perRank: 0.02 }, { key: "summonLifePct", perRank: 0.015 }] },
  { id: "cls_nec_inf_frenzy", classId: "necromancer", branch: "class", nameKey: "node.cls_nec_inf_frenzy", tier: 6, parent: "cls_nec_apex", maxRank: 50, cost: 1, icon: "⚡", effects: [{ key: "summonSpeedPct", perRank: 0.01 }, { key: "spellDamagePct", perRank: 0.015 }] },
  { id: "cls_nec_inf_blight", classId: "necromancer", branch: "class", nameKey: "node.cls_nec_inf_blight", tier: 6, parent: "cls_nec_apex", maxRank: 50, cost: 1, icon: "☠️", effects: [{ key: "vsElitePct", perRank: 0.006 }, { key: "spellDamagePct", perRank: 0.015 }] },
];

const NODE_BY_ID = new Map(VOID_NODES.map((n) => [n.id, n]));
export function voidNode(id: string): VoidNode | undefined {
  return NODE_BY_ID.get(id);
}
/** Pré-requisitos completos de um nó (pai + extras). */
export function nodeRequires(n: VoidNode): string[] {
  const out = n.parent ? [n.parent] : [];
  if (n.alsoRequires) out.push(...n.alsoRequires);
  return out;
}
/** Filhos diretos (para a UI desenhar os fios). */
export function nodeChildren(id: string): VoidNode[] {
  return VOID_NODES.filter((n) => n.parent === id);
}

/** Efeitos agregados da árvore (entram no recompute central). */
export interface TreeEffects {
  damagePct: number;
  critChance: number;
  critDamagePct: number;
  attackSpeedPct: number;
  attackRangeFlat: number;
  maxHpPct: number;
  armorFlat: number;
  moveSpeedPct: number;
  hookRangePct: number;
  hookCdReducPct: number;
  sprintDurationPct: number;
  sprintCdReducPct: number;
  vsElitePct: number;
  executePct: number;
  resiliencePct: number;
  dmgSlashPct: number;
  dmgPiercePct: number;
  dmgBluntPct: number;
  defSlashPct: number;
  defPiercePct: number;
  defBluntPct: number;
  blockChance: number;
  regenPct: number;
  lifeStealPct: number; // 10.9EQ
  spellDamagePct: number; // 10.9CL
  spellRadiusPct: number;
  spellCdReducPct: number;
  summonCount: number;
  summonLifePct: number;
  summonSpeedPct: number;
  summonHpPct: number; // 10.9CM
  spellHealOnCast: number;
  spellCritBonus: number;
  flags: Set<string>;
}

function emptyTreeEffects(): TreeEffects {
  return {
    damagePct: 0, critChance: 0, critDamagePct: 0, attackSpeedPct: 0, attackRangeFlat: 0, maxHpPct: 0, armorFlat: 0,
    moveSpeedPct: 0, hookRangePct: 0, hookCdReducPct: 0, sprintDurationPct: 0, sprintCdReducPct: 0,
    vsElitePct: 0, executePct: 0, resiliencePct: 0,
    dmgSlashPct: 0, dmgPiercePct: 0, dmgBluntPct: 0, defSlashPct: 0, defPiercePct: 0, defBluntPct: 0,
    blockChance: 0, regenPct: 0, lifeStealPct: 0,
    spellDamagePct: 0, spellRadiusPct: 0, spellCdReducPct: 0, summonCount: 0, summonLifePct: 0, summonSpeedPct: 0, // 10.9CL
    summonHpPct: 0, spellHealOnCast: 0, spellCritBonus: 0, // 10.9CM
    flags: new Set<string>(),
  };
}

export class VoidTreeState {
  private ranks = new Map<string, number>();
  /** 10.5L — pontos COMPRADOS na Loja (Fragmentos). Persistidos no progresso, não aqui. */
  private bonus = 0;

  /** Total de pontos GANHOS: derivado (nível + boss + comprados). */
  static earned(level: number, bossDefeated: boolean, bought = 0): number {
    return (
      VoidTreeState.fromLevel(level) +
      (bossDefeated ? GameConfig.voidTree.bossBonus : 0) +
      Math.max(0, Math.floor(bought))
    );
  }
  /** 10.9AQ — pontos vindos SÓ do nível: 1 a cada `levelsPerPoint` níveis acima do inicial. */
  static fromLevel(level: number): number {
    const per = Math.max(1, GameConfig.voidTree.levelsPerPoint);
    return Math.floor(Math.max(0, level - GameConfig.progression.startLevel) / per);
  }
  /** 10.9HU — quantos pontos FECHAM a árvore inteira (Poder+Sobrevivência+Agilidade+1 classe, com
   *  os nós "infinitos"): soma maxRank×cost de todo nó fora de "class", mais os da classe ativa.
   *  Usado como régua pra travar as salas de prestígio da Fenda dos Titãs por % da árvore. */
  static totalMaxPoints(classId: string | undefined): number {
    let s = 0;
    for (const n of VOID_NODES) {
      if (n.branch === "class" && n.classId !== classId) continue;
      s += n.maxRank * n.cost;
    }
    return s;
  }

  /** Pontos extras comprados (o Game injeta a partir do GameProgress). */
  setBoughtPoints(n: number): void {
    this.bonus = Math.max(0, Math.floor(n) || 0);
  }
  get boughtPoints(): number {
    return this.bonus;
  }

  rankOf(id: string): number {
    return this.ranks.get(id) ?? 0;
  }
  get totalNodesUnlocked(): number {
    let n = 0;
    for (const v of this.ranks.values()) if (v > 0) n++;
    return n;
  }
  spent(): number {
    let s = 0;
    for (const [id, rank] of this.ranks) {
      const node = voidNode(id);
      if (node) s += rank * node.cost;
    }
    return s;
  }
  earnedTotal(level: number, bossDefeated: boolean): number {
    return VoidTreeState.earned(level, bossDefeated, this.bonus);
  }
  available(level: number, bossDefeated: boolean): number {
    return this.earnedTotal(level, bossDefeated) - this.spent();
  }
  branchPoints(branch: Branch): number {
    let s = 0;
    for (const [id, rank] of this.ranks) {
      const node = voidNode(id);
      if (node && node.branch === branch) s += rank * node.cost;
    }
    return s;
  }

  requirementsMet(node: VoidNode): boolean {
    return nodeRequires(node).every((rid) => this.rankOf(rid) >= 1);
  }
  canUnlock(node: VoidNode, level: number, bossDefeated: boolean): boolean {
    if (this.rankOf(node.id) >= node.maxRank) return false;
    if (!this.requirementsMet(node)) return false;
    return this.available(level, bossDefeated) >= node.cost;
  }
  /** Compra um rank. Valida no MODEL (não confia na UI). */
  unlock(id: string, level: number, bossDefeated: boolean): boolean {
    const node = voidNode(id);
    if (!node) return false;
    if (!this.canUnlock(node, level, bossDefeated)) return false;
    this.ranks.set(id, this.rankOf(id) + 1);
    return true;
  }
  reset(): void {
    this.ranks.clear();
  }

  /** Agrega os efeitos numéricos + flags dos nós comprados. */
  effects(): TreeEffects {
    const e = emptyTreeEffects();
    for (const [id, rank] of this.ranks) {
      if (rank <= 0) continue;
      const node = voidNode(id);
      if (!node) continue;
      for (const eff of node.effects) e[eff.key] += eff.perRank * rank;
      if (node.flag) e.flags.add(node.flag);
    }
    return e;
  }

  snapshot(): Record<string, number> {
    const out: Record<string, number> = {};
    for (const [id, rank] of this.ranks) if (rank > 0) out[id] = rank;
    return out;
  }

  /** Carrega com validação: ignora ids inválidos, clampa ao maxRank; se gastar mais que o
   *  ganho (save antigo com árvore diferente / adulterado / ritmo de pontos mudou), zera a
   *  árvore (devolve os pontos). Retorna true se ZEROU (o Game avisa o jogador). */
  load(data: Record<string, number> | undefined, level: number, bossDefeated: boolean): boolean {
    this.ranks.clear();
    if (data) {
      for (const [id, rank] of Object.entries(data)) {
        const node = voidNode(id);
        if (!node) continue;
        const r = Math.min(node.maxRank, Math.max(0, Math.floor(Number(rank) || 0)));
        if (r > 0) this.ranks.set(id, r);
      }
    }
    if (this.spent() > this.earnedTotal(level, bossDefeated)) {
      const had = this.ranks.size > 0;
      this.ranks.clear();
      return had;
    }
    return false;
  }
}
